import {
  apiGet,
  apiSend,
  ApiRequestError as HttpApiRequestError,
} from "../../../api/authDevApiClient.js";
import { isPrivilegedSystemRole } from "../../../utils/appRole.js";
import { ApiRequestError } from "./client.tsx";
import type {
  AppUserRole,
  DateFilterPreset,
  ModuleCatalogEntry,
  ModuleKey,
  RoleListItem,
  RoleModulePermission,
  RolePermission,
  SaveRolePermissionsPayload,
  UserCameraOption,
  UserListItem,
  UserRoleOption,
} from "../types.tsx";

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: { code?: string; message?: string } | null;
  message?: string;
};

type ApiUserRecord = {
  id: string;
  email: string;
  fullName: string | null;
  role: string | null;
  roleId?: string | null;
  systemRole?: string | null;
  systemRoleLabel?: string | null;
  customRoleId?: string | null;
  customRoleName?: string | null;
  businessRoleId?: string | null;
  businessRoleName?: string | null;
  isActive: boolean;
  createdAt: string;
  passwordDisplay?: string;
  cameraIds?: string[];
  cameras?: { id: string; name: string }[];
};

type ApiRoleRecord = {
  id: string;
  roleId: string;
  role: string;
  slug?: string | null;
  isPredefined?: boolean;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  permissions?: RolePermission[];
  modulePermissions?: RoleModulePermission[];
  cameraPermissions?: RolePermission[];
  permissionSummary?: string;
};

type RoleOptionRow = UserRoleOption;

type UserListApiResult = {
  items: ApiUserRecord[];
  roleOptions: RoleOptionRow[];
  customRoleOptions?: RoleOptionRow[];
  cameraOptions?: UserCameraOption[];
  moduleCatalog?: ModuleCatalogEntry[];
  total: number;
  page?: number;
  limit?: number;
};

type RoleListApiResult = {
  items: ApiRoleRecord[];
  roleOptions?: RoleOptionRow[];
  moduleCatalog?: ModuleCatalogEntry[];
  total: number;
  page?: number;
  limit?: number;
};

const DEFAULT_MODULE_CATALOG: ModuleCatalogEntry[] = [
  { key: "cameras", label: "Camera Management" },
  { key: "ai_models", label: "AI Models Management" },
  { key: "rules", label: "Rule Management" },
  { key: "use_cases", label: "Use Case Configuration" },
  { key: "zones", label: "Zone Management" },
  { key: "users", label: "User Management" },
  { key: "roles", label: "Roles & Permissions" },
  { key: "reports", label: "Reports" },
];

const LEGACY_LS_USERS = "dt_admin_ura_users_v1";
const LEGACY_LS_ROLES = "dt_admin_ura_roles_v1";
const LEGACY_LS_CAMERAS = "dt_admin_ura_cameras_v1";

/** Drop seeded demo rows from older localStorage builds. */
export function clearLegacyDemoUserRoleStorage(): void {
  try {
    localStorage.removeItem(LEGACY_LS_USERS);
    localStorage.removeItem(LEGACY_LS_ROLES);
    localStorage.removeItem(LEGACY_LS_CAMERAS);
  } catch {
    /* ignore */
  }
}

clearLegacyDemoUserRoleStorage();

function rethrowAsUiError(e: unknown): never {
  if (e instanceof HttpApiRequestError) {
    throw new ApiRequestError(e.message, e.code);
  }
  throw e;
}

function unwrapEnvelope<T>(body: unknown): T {
  if (body && typeof body === "object") {
    const o = body as ApiEnvelope<T> & {
      errors?: { message?: string }[];
    };
    if (o.success === false) {
      const message = o.error?.message ?? o.message ?? "Request failed";
      const code = o.error?.code ?? "REQUEST_FAILED";
      throw new ApiRequestError(message, code);
    }
    if (Array.isArray(o.errors) && o.errors.length > 0) {
      const message = o.errors[0]?.message ?? o.message ?? "Request failed";
      throw new ApiRequestError(message, "REQUEST_FAILED");
    }
    if ("data" in o && o.data !== undefined) return o.data as T;
  }
  return body as T;
}

function normalizeCrudFlags(o: Record<string, unknown>): {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
} {
  return {
    canCreate: pickBoolField(o.canCreate ?? o.can_create),
    canRead: pickBoolField(o.canRead ?? o.can_read),
    canUpdate: pickBoolField(o.canUpdate ?? o.can_update),
    canDelete: pickBoolField(o.canDelete ?? o.can_delete),
  };
}

function normalizeCameraPermissionRow(raw: unknown): RolePermission | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const cameraId = String(o.cameraId ?? o.camera_id ?? "").trim();
  if (!cameraId) return null;
  return { cameraId, ...normalizeCrudFlags(o) };
}

function normalizeModulePermissionRow(raw: unknown): RoleModulePermission | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const module = String(o.module ?? o.moduleKey ?? o.module_key ?? "").trim() as ModuleKey;
  if (!module) return null;
  return { module, ...normalizeCrudFlags(o) };
}

function normalizeModuleCatalogEntry(raw: unknown): ModuleCatalogEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const key = String(o.key ?? o.module ?? o.moduleKey ?? "").trim() as ModuleKey;
  if (!key) return null;
  const label = String(o.label ?? o.name ?? key).trim();
  return { key, label: label || key };
}

function normalizeCameraOptionRow(raw: unknown): UserCameraOption | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? o.uuid ?? "").trim();
  if (!id) return null;
  return { id, name: String(o.name ?? o.label ?? id).trim() || id };
}

function pickBoolField(v: unknown): boolean {
  if (v === false || v === 0) return false;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "false" || s === "0") return false;
    if (s === "true" || s === "1") return true;
  }
  return Boolean(v);
}

function normalizeApiRoleRecord(raw: unknown): ApiRoleRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? o.uuid ?? "").trim();
  const roleId = String(o.roleId ?? o.role_id ?? "").trim();
  const resolvedId = id || roleId;
  if (!resolvedId) return null;
  const slug =
    o.slug != null && String(o.slug).trim() !== "" ? String(o.slug).trim() : null;
  return {
    id: resolvedId,
    roleId: roleId || resolvedId,
    role: String(o.role ?? "").trim() || roleId || resolvedId,
    slug,
    isPredefined: pickBoolField(o.isPredefined ?? o.is_predefined),
    description:
      o.description != null && String(o.description).trim() !== ""
        ? String(o.description)
        : null,
    isActive: pickBoolField(o.isActive ?? o.is_active ?? true),
    createdAt: String(o.createdAt ?? o.created_at ?? new Date().toISOString()),
    updatedAt: o.updatedAt != null ? String(o.updatedAt) : o.updated_at != null ? String(o.updated_at) : undefined,
    permissions: Array.isArray(o.permissions)
      ? (o.permissions as RolePermission[])
      : undefined,
    modulePermissions: Array.isArray(o.modulePermissions)
      ? (o.modulePermissions as RoleModulePermission[])
      : Array.isArray(o.module_permissions)
        ? (o.module_permissions as RoleModulePermission[])
        : undefined,
    cameraPermissions: Array.isArray(o.cameraPermissions)
      ? (o.cameraPermissions as RolePermission[])
      : Array.isArray(o.camera_permissions)
        ? (o.camera_permissions as RolePermission[])
        : undefined,
    permissionSummary:
      typeof o.permissionSummary === "string"
        ? o.permissionSummary
        : typeof o.permission_summary === "string"
          ? o.permission_summary
          : undefined,
  };
}

function normalizeRoleOptionRow(raw: unknown): RoleOptionRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? o.uuid ?? "").trim();
  const roleId = String(o.roleId ?? o.role_id ?? "").trim();
  const resolvedId = id || roleId;
  if (!resolvedId) return null;
  const systemRoleRaw = o.systemRole ?? o.system_role;
  const slugRaw = o.slug;
  const hasPredefined =
    o.isPredefined !== undefined || o.is_predefined !== undefined;
  return {
    id: resolvedId,
    roleId: roleId || undefined,
    role: String(o.role ?? "").trim() || roleId || resolvedId,
    ...(systemRoleRaw != null && String(systemRoleRaw).trim() !== ""
      ? { systemRole: String(systemRoleRaw).trim() }
      : {}),
    ...(slugRaw != null && String(slugRaw).trim() !== ""
      ? { slug: String(slugRaw).trim() }
      : {}),
    ...(hasPredefined
      ? { isPredefined: pickBoolField(o.isPredefined ?? o.is_predefined) }
      : {}),
  };
}

function normalizeApiUserRecord(raw: unknown): ApiUserRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? "").trim();
  if (!id) return null;
  return {
    id,
    email: String(o.email ?? ""),
    fullName: o.fullName != null ? String(o.fullName) : o.full_name != null ? String(o.full_name) : null,
    role: o.role != null ? String(o.role) : o.authRole != null ? String(o.authRole) : null,
    roleId:
      o.roleId != null
        ? String(o.roleId)
        : o.role_id != null
          ? String(o.role_id)
          : null,
    systemRole: o.systemRole != null ? String(o.systemRole) : o.system_role != null ? String(o.system_role) : null,
    systemRoleLabel:
      o.systemRoleLabel != null
        ? String(o.systemRoleLabel)
        : o.system_role_label != null
          ? String(o.system_role_label)
          : null,
    customRoleId:
      o.customRoleId != null
        ? String(o.customRoleId)
        : o.custom_role_id != null
          ? String(o.custom_role_id)
          : null,
    customRoleName:
      o.customRoleName != null
        ? String(o.customRoleName)
        : o.custom_role_name != null
          ? String(o.custom_role_name)
          : null,
    businessRoleId:
      o.businessRoleId != null
        ? String(o.businessRoleId)
        : o.business_role_id != null
          ? String(o.business_role_id)
          : null,
    businessRoleName:
      o.businessRoleName != null
        ? String(o.businessRoleName)
        : o.business_role_name != null
          ? String(o.business_role_name)
          : null,
    isActive: pickBoolField(o.isActive ?? o.is_active ?? true),
    createdAt: String(o.createdAt ?? o.created_at ?? new Date().toISOString()),
    passwordDisplay:
      typeof o.passwordDisplay === "string"
        ? o.passwordDisplay
        : typeof o.password_display === "string"
          ? o.password_display
          : undefined,
    cameraIds: Array.isArray(o.cameraIds)
      ? o.cameraIds.map((id) => String(id))
      : Array.isArray(o.camera_ids)
        ? o.camera_ids.map((id) => String(id))
        : undefined,
    cameras: Array.isArray(o.cameras)
      ? (o.cameras as { id: string; name: string }[])
      : undefined,
  };
}

function mergeRoleOptionRows(...groups: (RoleOptionRow | null)[][]): RoleOptionRow[] {
  const byId = new Map<string, RoleOptionRow>();
  for (const group of groups) {
    for (const row of group) {
      if (!row) continue;
      const existing = byId.get(row.id);
      if (!existing) {
        byId.set(row.id, row);
        continue;
      }
      byId.set(row.id, {
        ...existing,
        ...row,
        role: row.role || existing.role,
        systemRole: row.systemRole ?? existing.systemRole,
        slug: row.slug ?? existing.slug,
        isPredefined:
          row.isPredefined !== undefined ? row.isPredefined : existing.isPredefined,
      });
    }
  }
  return Array.from(byId.values());
}

function normalizeUserListResult(raw: UserListApiResult): UserListApiResult {
  const items = (Array.isArray(raw?.items) ? raw.items : [])
    .map(normalizeApiUserRecord)
    .filter((u): u is ApiUserRecord => u != null);
  const roleOptions = mergeRoleOptionRows(
    (Array.isArray(raw?.roleOptions) ? raw.roleOptions : []).map(normalizeRoleOptionRow),
    (Array.isArray(raw?.customRoleOptions) ? raw.customRoleOptions : []).map((r) => {
      const row = normalizeRoleOptionRow(r);
      return row ? { ...row, isPredefined: false } : null;
    }),
  );
  const cameraOptions = (Array.isArray(raw?.cameraOptions) ? raw.cameraOptions : [])
    .map(normalizeCameraOptionRow)
    .filter((c): c is UserCameraOption => c != null);
  const moduleCatalog = (Array.isArray(raw?.moduleCatalog) ? raw.moduleCatalog : [])
    .map(normalizeModuleCatalogEntry)
    .filter((m): m is ModuleCatalogEntry => m != null);
  return {
    items,
    roleOptions,
    cameraOptions,
    moduleCatalog: moduleCatalog.length > 0 ? moduleCatalog : undefined,
    total: typeof raw?.total === "number" ? raw.total : items.length,
    page: raw?.page,
    limit: raw?.limit,
  };
}

function normalizeRoleListResult(raw: RoleListApiResult): RoleListApiResult {
  const items = (Array.isArray(raw?.items) ? raw.items : [])
    .map(normalizeApiRoleRecord)
    .filter((r): r is ApiRoleRecord => r != null);

  let roleOptions = (Array.isArray(raw?.roleOptions) ? raw.roleOptions : [])
    .map(normalizeRoleOptionRow)
    .filter((o): o is RoleOptionRow => o != null);

  if (roleOptions.length === 0) {
    roleOptions = items
      .filter((r) => r.isActive)
      .map((r) => ({
        id: r.id,
        roleId: r.roleId,
        role: r.role,
        slug: r.slug ?? undefined,
        isPredefined: r.isPredefined,
      }));
  }

  const moduleCatalog = (Array.isArray(raw?.moduleCatalog) ? raw.moduleCatalog : [])
    .map(normalizeModuleCatalogEntry)
    .filter((m): m is ModuleCatalogEntry => m != null);

  return {
    items,
    roleOptions,
    moduleCatalog: moduleCatalog.length > 0 ? moduleCatalog : undefined,
    total: typeof raw?.total === "number" ? raw.total : items.length,
    page: raw?.page,
    limit: raw?.limit,
  };
}

export function resolveModuleCatalog(catalog?: ModuleCatalogEntry[]): ModuleCatalogEntry[] {
  return catalog && catalog.length > 0 ? catalog : DEFAULT_MODULE_CATALOG;
}

/** Active roles for user form dropdowns and filters. */
export async function adminFetchActiveRoleOptions(): Promise<UserRoleOption[]> {
  const res = await adminFetchRolesList({
    page: 1,
    limit: 100,
    dateFilter: "all",
    sortBy: "role",
    sortDir: "asc",
  });
  return res.items
    .filter((r) => r.isActive)
    .map((r) => ({
      id: r.id,
      role: r.role,
      slug: r.slug ?? undefined,
      isPredefined: r.isPredefined,
      systemRole:
        r.isPredefined && r.slug && isPrivilegedSystemRole(r.slug) ? r.slug : undefined,
    }));
}

function mapAuthRole(role: string | null | undefined): AppUserRole {
  return role === "admin" ? "admin" : "end_user";
}

function permissionSummary(
  modulePerms: RoleModulePermission[],
  moduleCatalog: ModuleCatalogEntry[],
): string {
  const modulesWithAccess = modulePerms.filter(
    (p) => p.canCreate || p.canRead || p.canUpdate || p.canDelete,
  );
  if (modulesWithAccess.length === 0) return "None";
  const labelByKey = new Map(moduleCatalog.map((m) => [m.key, m.label]));
  const labels = modulesWithAccess.map((p) => labelByKey.get(p.module) ?? p.module);
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

/** Resolve dropdown roleId for edit/create forms (system Admin vs custom business role). */
export function resolveUserFormRoleId(
  u: Pick<UserListItem, "businessRoleId" | "systemRoleLabel" | "role">,
  roleOptions: UserRoleOption[],
): string {
  const candidates = [
    u.businessRoleId?.trim(),
  ].filter((id): id is string => Boolean(id));

  for (const id of candidates) {
    if (roleOptions.some((o) => o.id === id)) return id;
  }

  const labelCandidates = [u.systemRoleLabel?.trim(), u.role === "admin" ? "Admin" : null].filter(
    (l): l is string => Boolean(l),
  );
  for (const label of labelCandidates) {
    const matches = roleOptions.filter((o) => o.role.toLowerCase() === label.toLowerCase());
    if (matches.length === 1) return matches[0]!.id;
    if (matches.length > 1) {
      const privileged = matches.find(
        (o) =>
          (o.systemRole && isPrivilegedSystemRole(o.systemRole)) ||
          (o.slug && isPrivilegedSystemRole(o.slug)),
      );
      if (privileged) return privileged.id;
      const predefined = matches.find((o) => o.isPredefined === true);
      if (predefined) return predefined.id;
      return matches[0]!.id;
    }
  }

  if (u.role === "admin") {
    const adminOpts = roleOptions.filter((o) => o.role.toLowerCase() === "admin");
    const privileged =
      adminOpts.find((o) => o.systemRole && isPrivilegedSystemRole(o.systemRole)) ??
      adminOpts.find((o) => o.isPredefined === true);
    if (privileged) return privileged.id;
    if (adminOpts[0]) return adminOpts[0].id;
  }

  return candidates[0] ?? "";
}

export function mapApiUserToListItem(u: ApiUserRecord, roleNameById: Map<string, string>): UserListItem {
  const businessRoleId =
    (u.roleId && String(u.roleId).trim()) ||
    (u.customRoleId && String(u.customRoleId).trim()) ||
    (u.businessRoleId && String(u.businessRoleId).trim()) ||
    undefined;
  const businessRoleName =
    (u.customRoleName && String(u.customRoleName).trim()) ||
    (u.businessRoleName && String(u.businessRoleName).trim()) ||
    (businessRoleId ? roleNameById.get(businessRoleId) ?? null : null) ||
    null;
  const systemRoleLabel =
    (u.systemRoleLabel && String(u.systemRoleLabel).trim()) || null;
  return {
    id: u.id,
    fullName: u.fullName,
    email: u.email,
    role: mapAuthRole(u.role),
    businessRoleId,
    businessRoleName,
    systemRoleLabel,
    passwordDisplay: u.passwordDisplay ?? "********",
    createdAt: u.createdAt,
    isActive: u.isActive,
    cameraIds: u.cameraIds,
  };
}

export function mapApiRoleToListItem(
  r: ApiRoleRecord,
  moduleCatalog: ModuleCatalogEntry[] = DEFAULT_MODULE_CATALOG,
): RoleListItem {
  const cameraPermissions = (
    r.cameraPermissions ??
    r.permissions ??
    []
  )
    .map((p) => normalizeCameraPermissionRow(p))
    .filter((p): p is RolePermission => p != null);

  const modulePermissions = (r.modulePermissions ?? [])
    .map((p) => normalizeModulePermissionRow(p))
    .filter((p): p is RoleModulePermission => p != null);

  return {
    id: r.id,
    roleId: r.roleId,
    role: r.role,
    slug: r.slug ?? null,
    isPredefined: r.isPredefined,
    description: r.description ?? null,
    permissions: cameraPermissions,
    modulePermissions,
    cameraPermissions,
    permissionSummary:
      r.permissionSummary ?? permissionSummary(modulePermissions, moduleCatalog),
    createdAt: r.createdAt,
    isActive: r.isActive,
  };
}

export function roleOptionsFromApiRoles(roles: RoleListItem[]): {
  roleNameById: Map<string, string>;
  roleOptions: UserRoleOption[];
} {
  const roleNameById = new Map<string, string>();
  const roleOptions: UserRoleOption[] = [];
  for (const r of roles) {
    if (!r.isActive) continue;
    roleNameById.set(r.id, r.role);
    roleOptions.push({
      id: r.id,
      role: r.role,
      slug: r.slug ?? undefined,
      isPredefined: r.isPredefined,
      systemRole:
        r.isPredefined && r.slug && isPrivilegedSystemRole(r.slug) ? r.slug : undefined,
    });
  }
  return { roleNameById, roleOptions };
}

type ApiDateFilter = Exclude<DateFilterPreset, "all">;

function toApiDateFilter(
  preset: DateFilterPreset,
  dateFrom?: string,
  dateTo?: string,
): { dateFilter: ApiDateFilter; dateFrom?: string; dateTo?: string } {
  if (preset === "all") {
    return { dateFilter: "custom", dateFrom: "2000-01-01", dateTo: "2099-12-31" };
  }
  if (preset === "custom") {
    return { dateFilter: "custom", dateFrom, dateTo };
  }
  return { dateFilter: preset };
}

function usersQuery(params: {
  page: number;
  limit: number;
  search?: string;
  roles?: string[];
  status?: "active" | "inactive";
  dateFilter: DateFilterPreset;
  dateFrom?: string;
  dateTo?: string;
  sortBy: string;
  sortDir: string;
}): string {
  const q = new URLSearchParams();
  q.set("page", String(params.page));
  q.set("limit", String(params.limit));
  if (params.search?.trim()) q.set("search", params.search.trim());
  if (params.roles?.length) q.set("roles", params.roles.join(","));
  if (params.status) q.set("status", params.status);
  const dates = toApiDateFilter(params.dateFilter, params.dateFrom, params.dateTo);
  q.set("dateFilter", dates.dateFilter);
  if (dates.dateFilter === "custom") {
    if (dates.dateFrom) q.set("dateFrom", dates.dateFrom);
    if (dates.dateTo) q.set("dateTo", dates.dateTo);
  }
  q.set("sortBy", params.sortBy);
  q.set("sortDir", params.sortDir);
  return q.toString();
}

function rolesQuery(params: {
  page: number;
  limit: number;
  search?: string;
  roleIds?: string[];
  status?: "active" | "inactive";
  dateFilter: DateFilterPreset;
  dateFrom?: string;
  dateTo?: string;
  sortBy: string;
  sortDir: string;
}): string {
  const q = new URLSearchParams();
  q.set("page", String(params.page));
  q.set("limit", String(params.limit));
  if (params.search?.trim()) q.set("search", params.search.trim());
  if (params.roleIds?.length) q.set("roleIds", params.roleIds.join(","));
  if (params.status) q.set("status", params.status);
  const dates = toApiDateFilter(params.dateFilter, params.dateFrom, params.dateTo);
  q.set("dateFilter", dates.dateFilter);
  if (dates.dateFilter === "custom") {
    if (dates.dateFrom) q.set("dateFrom", dates.dateFrom);
    if (dates.dateTo) q.set("dateTo", dates.dateTo);
  }
  q.set("sortBy", params.sortBy);
  q.set("sortDir", params.sortDir);
  return q.toString();
}

export async function adminFetchUsersList(params: {
  page: number;
  limit: number;
  search?: string;
  roles?: string[];
  status?: "active" | "inactive";
  dateFilter: DateFilterPreset;
  dateFrom?: string;
  dateTo?: string;
  sortBy: string;
  sortDir: string;
}): Promise<UserListApiResult> {
  try {
    const body = await apiGet(`/api/users?${usersQuery(params)}`);
    return normalizeUserListResult(unwrapEnvelope<UserListApiResult>(body));
  } catch (e) {
    rethrowAsUiError(e);
  }
}

export async function adminFetchRolesList(params: {
  page: number;
  limit: number;
  search?: string;
  roleIds?: string[];
  status?: "active" | "inactive";
  dateFilter: DateFilterPreset;
  dateFrom?: string;
  dateTo?: string;
  sortBy: string;
  sortDir: string;
}): Promise<RoleListApiResult> {
  try {
    const body = await apiGet(`/api/roles?${rolesQuery(params)}`);
    return normalizeRoleListResult(unwrapEnvelope<RoleListApiResult>(body));
  } catch (e) {
    rethrowAsUiError(e);
  }
}

export async function adminListUserRecords(): Promise<ApiUserRecord[]> {
  const res = await adminFetchUsersList({
    page: 1,
    limit: 100,
    dateFilter: "all",
    sortBy: "createdAt",
    sortDir: "desc",
  });
  return res.items;
}

export async function adminListRoleRecords(): Promise<ApiRoleRecord[]> {
  const res = await adminFetchRolesList({
    page: 1,
    limit: 100,
    dateFilter: "all",
    sortBy: "createdAt",
    sortDir: "desc",
  });
  return res.items;
}

export async function adminGetUser(id: string): Promise<ApiUserRecord | null> {
  try {
    const body = await apiGet(`/api/users/${encodeURIComponent(id)}`);
    const raw = unwrapEnvelope<unknown>(body);
    return normalizeApiUserRecord(raw);
  } catch (e) {
    if (e instanceof HttpApiRequestError && (e.code === "NOT_FOUND" || e.status === 404)) return null;
    rethrowAsUiError(e);
  }
}

export async function adminGetRole(id: string): Promise<ApiRoleRecord | null> {
  try {
    const body = await apiGet(`/api/roles/${encodeURIComponent(id)}`);
    return normalizeApiRoleRecord(unwrapEnvelope<unknown>(body));
  } catch (e) {
    if (e instanceof HttpApiRequestError && (e.code === "NOT_FOUND" || e.status === 404)) return null;
    rethrowAsUiError(e);
  }
}

async function apiSendEnvelope<T>(path: string, method: "POST" | "PATCH" | "PUT" | "DELETE", body?: unknown): Promise<T> {
  try {
    const res = await apiSend(path, method, body);
    return unwrapEnvelope<T>(res);
  } catch (e) {
    rethrowAsUiError(e);
  }
}

export async function adminCreateUser(body: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roleId: string;
  cameraIds?: string[];
}): Promise<void> {
  await apiSendEnvelope("/api/users", "POST", body);
}

export async function adminUpdateUser(
  id: string,
  body: {
    firstName: string;
    lastName: string;
    roleId: string;
    email: string;
    password?: string;
    cameraIds?: string[];
  },
): Promise<void> {
  const payload: Record<string, unknown> = {
    firstName: body.firstName,
    lastName: body.lastName,
    roleId: body.roleId,
    email: body.email,
  };
  if (body.password?.trim()) payload.password = body.password.trim();
  if (body.cameraIds !== undefined) payload.cameraIds = body.cameraIds;
  await apiSendEnvelope(`/api/users/${encodeURIComponent(id)}`, "PUT", payload);
}

export async function adminPatchUserStatus(id: string, isActive: boolean): Promise<ApiUserRecord | null> {
  return apiSendEnvelope<ApiUserRecord>(`/api/users/${encodeURIComponent(id)}/status`, "PATCH", { isActive });
}

export async function adminDeleteUser(id: string): Promise<void> {
  try {
    await apiSend(`/api/users/${encodeURIComponent(id)}`, "DELETE");
  } catch (e) {
    rethrowAsUiError(e);
  }
}

export async function adminCreateRole(role: string, description?: string): Promise<void> {
  await apiSendEnvelope("/api/roles", "POST", {
    role,
    ...(description ? { description } : {}),
  });
}

export async function adminUpdateRole(id: string, role: string, description?: string): Promise<void> {
  await apiSendEnvelope(`/api/roles/${encodeURIComponent(id)}`, "PUT", {
    role,
    ...(description ? { description } : {}),
  });
}

export async function adminPatchRoleStatus(id: string, isActive: boolean): Promise<ApiRoleRecord | null> {
  return apiSendEnvelope<ApiRoleRecord>(`/api/roles/${encodeURIComponent(id)}/status`, "PATCH", { isActive });
}

export async function adminDeleteRole(id: string): Promise<void> {
  try {
    await apiSend(`/api/roles/${encodeURIComponent(id)}`, "DELETE");
  } catch (e) {
    rethrowAsUiError(e);
  }
}

export async function adminReplaceRolePermissions(
  roleId: string,
  payload: SaveRolePermissionsPayload,
): Promise<ApiRoleRecord | null> {
  return apiSendEnvelope<ApiRoleRecord>(`/api/roles/${encodeURIComponent(roleId)}/permissions`, "POST", {
    modulePermissions: payload.modulePermissions,
  });
}
